import { createSlice } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '../../shared/utils/constants';

/**
 * Initial state for orchestration management
 */
const initialState = {
  // List of all orchestrations
  orchestrations: [],

  // Currently selected orchestration ID
  selectedOrchestrationId: null,

  // Currently selected step index within the orchestration
  selectedStepIndex: null,

  // Loading state for orchestration data fetching
  isLoading: false,

  // Error message for orchestration operations
  error: null,

  // Execution state for tracking step/orchestration execution
  executionState: {
    isExecuting: false,
    executingStepIndex: null,
    error: null,
  },
};

/**
 * Orchestration slice for managing orchestration state
 */
const orchestrationSlice = createSlice({
  name: 'orchestration',
  initialState,
  reducers: {
    /**
     * Set the list of orchestrations
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: array of orchestration objects
     */
    setOrchestrations: (state, action) => {
      // Deep clone the orchestrations to avoid mutating frozen objects from API response
      // Clear api_response from all steps when loading orchestrations
      // This ensures stale responses don't appear when the app is refreshed
      const orchestrations = action.payload;
      if (Array.isArray(orchestrations)) {
        // Create a deep copy and clear api_response
        state.orchestrations = orchestrations.map(orch => {
          const orchCopy = { ...orch };
          if (orchCopy.steps && Array.isArray(orchCopy.steps)) {
            orchCopy.steps = orchCopy.steps.map(step => ({
              ...step,
              api_response: null,
            }));
          }
          return orchCopy;
        });
      } else {
        state.orchestrations = orchestrations;
      }
      state.error = null;
    },

    /**
     * Select an orchestration by ID
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: orchestration ID (number)
     */
    selectOrchestration: (state, action) => {
      const previousOrchestrationId = state.selectedOrchestrationId;
      state.selectedOrchestrationId = action.payload;
      state.error = null;

      // Clear api_response from all steps when selecting an orchestration
      // This ensures stale responses don't appear when revisiting after refresh
      if (action.payload) {
        const orchestrationIndex = state.orchestrations.findIndex(
          (o) => o.orchestration_id === action.payload
        );
        if (orchestrationIndex !== -1 && state.orchestrations[orchestrationIndex].steps) {
          // Use Immer's draft state properly - we can mutate state.orchestrations directly
          state.orchestrations[orchestrationIndex].steps = state.orchestrations[orchestrationIndex].steps.map(step => ({
            ...step,
            api_response: null,
          }));

          // Auto-select first step when switching between orchestrations
          // Only auto-select if:
          // 1. We're switching FROM one orchestration TO another (not initial load)
          // 2. The new orchestration has at least one step
          const isSwitchingOrchestrations = previousOrchestrationId !== null && 
                                           previousOrchestrationId !== action.payload;
          const hasSteps = state.orchestrations[orchestrationIndex].steps.length > 0;

          if (isSwitchingOrchestrations && hasSteps) {
            // Auto-select the first step (index 0)
            state.selectedStepIndex = 0;
          } else {
            // Reset step selection for initial load or when no steps exist
            state.selectedStepIndex = null;
          }
        } else {
          // No steps found, reset step selection
          state.selectedStepIndex = null;
        }
      } else {
        // Deselecting orchestration, reset step selection
        state.selectedStepIndex = null;
      }
    },

    /**
     * Select a step by index within the current orchestration
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: step index (number)
     */
    selectStep: (state, action) => {
      state.selectedStepIndex = action.payload;
      state.error = null;
    },

    /**
     * Update a step within an orchestration
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { orchestrationId, stepIndex, updates }
     */
    updateStep: (state, action) => {
      const { orchestrationId, stepIndex, updates } = action.payload;

      // Find the orchestration
      const orchestration = state.orchestrations.find(
        (o) => o.orchestration_id === orchestrationId
      );

      if (orchestration && orchestration.steps && orchestration.steps[stepIndex]) {
        // Deep merge updates into the step
        const step = orchestration.steps[stepIndex];

        // Update api_request fields if provided
        if (updates.api_request) {
          step.api_request = {
            ...step.api_request,
            ...updates.api_request,
          };
        }

        // Update other step fields
        Object.keys(updates).forEach((key) => {
          if (key !== 'api_request') {
            step[key] = updates[key];
          }
        });
      }

      state.error = null;
    },

    /**
     * Set the response for a specific step
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { orchestrationId, stepIndex, response }
     */
    setStepResponse: (state, action) => {
      const { orchestrationId, stepIndex, response } = action.payload;

      // Find the orchestration
      const orchestration = state.orchestrations.find(
        (o) => o.orchestration_id === orchestrationId
      );

      if (orchestration && orchestration.steps && orchestration.steps[stepIndex]) {
        orchestration.steps[stepIndex].api_response = response;
      }

      state.error = null;
    },

    /**
     * Set the orchestration execution ID for tracking sequential step execution
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { orchestrationId, orchestrationExecutionId }
     */
    setOrchestrationExecutionId: (state, action) => {
      const { orchestrationId, orchestrationExecutionId } = action.payload;

      // Find the orchestration
      const orchestration = state.orchestrations.find(
        (o) => o.orchestration_id === orchestrationId
      );

      if (orchestration) {
        orchestration.orchestration_execution_id = orchestrationExecutionId;
      }

      state.error = null;
    },

    /**
     * Set the loading state
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: boolean
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    /**
     * Set an error message
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: error message string or null
     */
    setError: (state, action) => {
      state.error = action.payload;
    },

    /**
     * Set the execution state
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { isExecuting, stepIndex?, error? }
     */
    setExecuting: (state, action) => {
      const { isExecuting, stepIndex = null, error = null } = action.payload;

      state.executionState = {
        isExecuting,
        executingStepIndex: stepIndex,
        error,
      };
    },

    /**
     * Reset orchestration state to initial values
     * @param {Object} state - Current state
     */
    resetOrchestration: (state) => {
      state.orchestrations = [];
      state.selectedOrchestrationId = null;
      state.selectedStepIndex = null;
      state.isLoading = false;
      state.error = null;
      state.executionState = {
        isExecuting: false,
        executingStepIndex: null,
        error: null,
      };
    },

    /**
     * Clear orchestration cache from sessionStorage
     * This should be called on logout
     * @param {Object} state - Current state
     */
    clearOrchestrationCache: (state) => {
      try {
        sessionStorage.removeItem(STORAGE_KEYS.ORCHESTRATIONS);
      } catch (error) {
        console.error('Error clearing orchestration cache:', error);
      }

      // Also reset state
      state.orchestrations = [];
      state.selectedOrchestrationId = null;
      state.selectedStepIndex = null;
      state.isLoading = false;
      state.error = null;
      state.executionState = {
        isExecuting: false,
        executingStepIndex: null,
        error: null,
      };
    },
  },
});

// Export actions
export const {
  setOrchestrations,
  selectOrchestration,
  selectStep,
  updateStep,
  setStepResponse,
  setOrchestrationExecutionId,
  setLoading,
  setError,
  setExecuting,
  resetOrchestration,
  clearOrchestrationCache,
} = orchestrationSlice.actions;

// Selectors
export const selectOrchestrations = (state) => state.orchestration.orchestrations;
export const selectSelectedOrchestrationId = (state) => state.orchestration.selectedOrchestrationId;
export const selectSelectedStepIndex = (state) => state.orchestration.selectedStepIndex;
export const selectIsLoading = (state) => state.orchestration.isLoading;
export const selectError = (state) => state.orchestration.error;
export const selectExecutionState = (state) => state.orchestration.executionState;

/**
 * Selector to get the currently selected orchestration object
 */
export const selectSelectedOrchestration = (state) => {
  const orchestrations = selectOrchestrations(state);
  const selectedId = selectSelectedOrchestrationId(state);

  if (!selectedId) return null;

  return orchestrations.find((o) => o.orchestration_id === selectedId) || null;
};

/**
 * Selector to get the currently selected step object
 */
export const selectSelectedStep = (state) => {
  const orchestration = selectSelectedOrchestration(state);
  const stepIndex = selectSelectedStepIndex(state);

  if (!orchestration || stepIndex === null || !orchestration.steps) return null;

  return orchestration.steps[stepIndex] || null;
};

// Export reducer
export default orchestrationSlice.reducer;
